import { formatBpsValue } from "~/utils/formatBpsValue";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";
import type { ScreenerAsset } from "./types";

// // // //

export function formatAUM(aum: number | null): string {
    if (aum === null || aum <= 0) {
        return "—";
    }

    if (aum >= 1_000_000_000) {
        return `$${(aum / 1_000_000_000).toFixed(1)}B`;
    }

    if (aum >= 1_000_000) {
        return `$${(aum / 1_000_000).toFixed(1)}M`;
    }

    return `$${Math.round(aum).toLocaleString()}`;
}

export function formatHoldersCount(count: number | null): string {
    if (count === null || count <= 0) {
        return "—";
    }

    return Math.round(count).toLocaleString();
}

export function formatMinInvestment(
    minInvestment: ScreenerAsset["minInvestment"],
): string {
    if (minInvestment.amount === null || minInvestment.amount < 0) {
        return "None";
    }

    return `${Math.round(minInvestment.amount).toLocaleString()} ${minInvestment.currency}`;
}

export function formatFee(fee: number | null): string {
    if (fee === null) {
        return "—";
    }

    return `${formatPercentValue(formatBpsValue(fee) * 100)}%`;
}

export function formatList(values: string[]): string {
    if (values.length === 0) {
        return "—";
    }

    return values.join(", ");
}

export function formatNetworks(networks: ScreenerAsset["networks"]): string {
    if (networks.length === 0) {
        return "—";
    }

    return networks.map((network) => network.name).join(", ");
}

export function formatLiquidity(value: string | null): string {
    return value?.replace(/\s*\([^)]*\)/g, "").trim() || "—";
}

export function clampValue(value: number, min: number, max: number) {
    if (Number.isNaN(value)) return min;
    return Math.min(Math.max(value, min), max);
}

export function normalizeRange(
    range: [number, number],
    min: number,
    max: number,
): [number, number] {
    const nextMin = clampValue(range[0], min, max);
    const nextMax = clampValue(range[1], min, max);
    return nextMin <= nextMax ? [nextMin, nextMax] : [nextMax, nextMin];
}

export function getInputValue(value: string, fallback: number) {
    if (value.trim() === "") return fallback;
    return Number(value);
}

export function formatIntegerInputValue(value: number) {
    if (!Number.isFinite(value)) {
        return "";
    }

    return Math.round(value).toLocaleString("en-US");
}

export function getIntegerInputValue(value: string, fallback: number) {
    const cleaned = value.replace(/,/g, "").trim();

    if (cleaned === "") {
        return fallback;
    }

    const parsed = Number(cleaned);

    if (Number.isNaN(parsed)) {
        return fallback;
    }

    return parsed;
}

export function formatSinceInceptionPeriod(
    periodStart: string | null,
    periodEnd: string | null,
): string | null {
    if (!periodStart || !periodEnd) {
        return null;
    }

    const formatDate = (value: string) =>
        new Date(value).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });

    return `Since inception (${formatDate(periodStart)} – ${formatDate(periodEnd)})`;
}
